import { useCallback, useMemo, useState } from 'react';
import { useNavigationBlocker } from 'hooks/useNavigationBlocker';

interface UseEditorUnsavedChangesGuardProps {
    editor: any;
    savedConfig?: Record<string, any> | null;
}

export const useEditorUnsavedChangesGuard = ({ editor, savedConfig }: UseEditorUnsavedChangesGuardProps) => {
    const [pendingCallback, setPendingCallback] = useState<(() => void) | null>(null);

    const hasUnsavedChanges = useMemo(() => () => {
        if (!editor) return false;

        const customJson = editor.toCustomJSON();
        if (!customJson?.jsonConfig?.nodes) return false;

        const jsonConfig = customJson.jsonConfig;
        const hasNodes = Object.keys(jsonConfig.nodes).length > 0;

        if (savedConfig) {
            return hasNodes && JSON.stringify(savedConfig) !== JSON.stringify(jsonConfig);
        }

        return hasNodes;
    }, [editor, savedConfig]);

    const { blocker, setBlockerActive } = useNavigationBlocker(hasUnsavedChanges);

    const guardedAction = useCallback((callback: () => void) => {
        if (hasUnsavedChanges()) {
            setPendingCallback(() => callback);
        } else {
            callback();
        }
    }, [hasUnsavedChanges]);

    const handleCancelDialog = useCallback(() => {
        if (blocker.state === 'blocked') {
            blocker.reset?.();
        }
        setPendingCallback(null);
    }, [blocker]);

    const handleConfirmDialog = useCallback(() => {
        if (blocker.state === 'blocked') {
            blocker.proceed?.();
        }

        if (pendingCallback) {
            pendingCallback();
            setPendingCallback(null);
        }
    }, [blocker, pendingCallback]);

    const isDialogOpen = blocker.state === 'blocked' || pendingCallback !== null;

    return {
        isDialogOpen,
        guardedAction,
        handleCancelDialog,
        handleConfirmDialog,
        setBlockerActive
    };
};